import { createTheme, alpha } from '@mui/material/styles';

// timing.kiwi brand pack v1 (see brandkit/timing-kiwi-brand-pack.html).
// Two greens do the work: Kiwi Green is the accent on dark surfaces only
// (it fails contrast on light backgrounds), Deep Green is its accessible
// substitute for text/UI on light surfaces, and Track Green is reserved
// for the logo mark and large graphic elements.
export const brand = {
  kiwiGreen: '#7CFC5A',
  trackGreen: '#3FAE2C',
  deepGreen: '#2E7D22',
  night: '#0C1210',
  chalk: '#F4F7F5',
  slate: '#8AA197',
};

const fontFamily = "'Chakra Petch', 'Segoe UI', system-ui, sans-serif";

const mainTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? brand.deepGreen : brand.kiwiGreen,
      light: mode === 'light' ? brand.trackGreen : brand.kiwiGreen,
    },
    secondary: {
      main: brand.slate,
    },
    background: {
      default: mode === 'light' ? brand.chalk : brand.night,
      // Dark surfaces stay flat Night rather than a barely-lighter tint —
      // two near-black greens stacked read as murky, not "raised". Cards
      // get separation from a border (see MuiPaper below), not a shade step.
      paper: mode === 'light' ? '#FFFFFF' : brand.night,
    },
    text: {
      primary: mode === 'light' ? brand.night : brand.chalk,
      secondary: brand.slate,
    },
  },
  typography: {
    fontFamily,
    h1: { fontFamily, fontWeight: 700, fontSize: '2.75rem', letterSpacing: '0.5px' }, // Display
    h2: { fontFamily, fontWeight: 700, fontSize: '1.75rem' }, // Heading
    overline: { fontFamily, fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '1.5px' }, // Subhead
    body1: { fontFamily, fontWeight: 400, fontSize: '1rem', lineHeight: 1.6 },
    caption: { fontFamily, fontWeight: 500, fontSize: '0.8125rem' }, // Caption / meta
  },
  components: {
    // Theme-aware thin scrollbar. An opaque track (not `transparent`) matters:
    // the scrollbar gutter is painted outside the document, so a transparent
    // track shows raw browser chrome, not the page — a glaring blank strip
    // next to a solid-colour AppBar. Track matches the page background,
    // which in dark mode is the same Night as the AppBar, so it disappears
    // entirely there; in light mode it still beats the old transparent gap.
    MuiCssBaseline: {
      styleOverrides: {
        '::-webkit-scrollbar': {
          width: 12,
          height: 12,
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: mode === 'light' ? brand.chalk : brand.night,
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: brand.slate,
          borderRadius: 8,
        },
      },
    },
    // Nav bars stay on Night regardless of light/dark mode, matching the
    // brand pack's own dark cover treatment — Kiwi Green/Chalk read best there.
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.night,
          color: brand.chalk,
        },
      },
    },
    // In dark mode, paper == the page background (see above), so give
    // cards a hairline border for separation instead of a shade step.
    MuiPaper: {
      styleOverrides: {
        root: mode === 'dark' ? {
          backgroundImage: 'none', // suppress MUI's default elevation overlay (another shade step)
          border: `1px solid ${alpha(brand.chalk, 0.1)}`,
        } : {},
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          wordBreak: 'break-word',
          whiteSpace: 'normal'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: { // Target table header cells
          fontSize: '0.75rem', // Smaller font size
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '1rem', // Smaller icons
        },
      },
    },
  }
});

export default mainTheme
